// Pure depth / surface-overlap math extracted 1:1 from RoomCanvas.tsx.
// No PIXI import — uses SceneNode duck-type (PIXI.Container satisfies structurally).
import { readFurnitureData, sceneTintNumber, type SceneNode, type SceneNodeChild } from './sceneNode';
import { exceedsTolerance, footprintOutOfRoom, placedFootprintTiles, placedRectTiles } from './footprintTolerance';
import { mirrorFpOffset, estimateRugFootprintTiles, isWallMountedItem, estimateWindowFootprintTiles, getTileDims } from './tileMath';
import { TILE_WIDTH, TILE_HEIGHT, MAP_WIDTH_TILES, GRID_SUBDIVISION } from './windowSnap';
import { type PlacementMode, toPlacementMode, isSurface, participatesInFootprint } from './placement';

// Z-index for floor items in footprint conflict. Must be: max_depth < CONFLICT_FLOOR_ZINDEX < SURFACE_ZINDEX_BOOST.
// Max depth ≈ room max pixel-Y ≈ 500; 1000 is safely between floor and surface layers.
const CONFLICT_FLOOR_ZINDEX = 1000;
// Tiny delta used in recomputeAllZIndexes for PLACED items. An item on a surface
// should be just barely above its owner, inheriting the owner's depth order relative
// to all other furniture (like CSS z-index with parent context). Value < min inter-tile
// depth difference (~12.5px) so the item stays in the correct depth band.
// Nested surfaces each add one more delta: owner+0, child+0.5, grandchild+1, etc.
const SURFACE_PLACEMENT_DELTA = 0.5;
// How many levels of "item on item on item" propagateSurfaceBoost resolves.
const MAX_SURFACE_LEVELS = 5;
/**
 * Everything standing on the floor — furniture AND the character — is sorted by
 * ONE painter key. An item that rides a surface is not a separate layer: it is
 * its owner's key + a fraction, so it inherits the owner's place in that one
 * order (see propagateSurfaceBoost).
 *
 * Consequence for anything that steps OVER a floor item: it must clear the whole
 * band the item's surface stack occupies, not just the item itself. A character
 * standing in front of a table is in front of the vase on that table too — the
 * vase's ground position IS the table. +0.001 used to leave the vase painted over
 * the character's chest; this constant is the fix.
 *
 * Bounded by the nesting cap, and strictly below the inter-tile depth step
 * (TILE_HEIGHT/2 = 6.25) so clearing one item can never jump the key past a
 * genuinely nearer tile.
 */
const SURFACE_STACK_CLEARANCE = SURFACE_PLACEMENT_DELTA * (MAX_SURFACE_LEVELS + 1);

// Type alias for the surface-tile lookup map built from all placed furniture.
export type SurfaceTileEntry = {
  ownerMarker: SceneNode;
  ownerZIndex: number;
  ownerFurnitureId: number; // prevents an item from being "on its own surface"
  ownerTileCount: number;   // surface tile count for size constraint
  ownerPlacementMode: PlacementMode; // used to prefer floor owners over boosted surface siblings
};
export type SurfaceTileMap = Map<string, SurfaceTileEntry>;

/**
 * Pure helper – no React state deps.
 * Checks whether a rectangular item footprint (baseTileX..+w × baseTileY..+h)
 * overlaps any surface tile in surfaceMap.
 * @param excludeFurnitureId  – skip tiles owned by this ID (prevents self-boost)
 * @param itemTileCount       – if set, skip surfaces where item is larger than surface
 * Returns { isOnSurface, maxOwnerZIndex }.
 */
export function checkSurfaceOverlap(
  baseTileX: number,
  baseTileY: number,
  widthTiles: number,
  heightTiles: number,
  surfaceMap: SurfaceTileMap,
  excludeFurnitureId?: number,
  itemTileCount?: number,
): { isOnSurface: boolean; maxOwnerZIndex: number } {
  // Use the highest owner zIndex across all valid surface tiles.
  // The iterative loop in recomputeAllZIndexes converges multi-level nesting
  // correctly with max-owner: each iteration raises one nesting level until stable.
  // ownerPlacementMode is stored for potential future use but not used here.
  let maxOwnerZIndex = -Infinity;
  let isOnSurface = false;
  for (let tx = 0; tx < widthTiles; tx++) {
    for (let ty = 0; ty < heightTiles; ty++) {
      const entry = surfaceMap.get(`${baseTileX + tx},${baseTileY + ty}`);
      if (entry) {
        // Don't count a furniture item as sitting on its own surface
        if (excludeFurnitureId !== undefined && entry.ownerFurnitureId === excludeFurnitureId) continue;
        // Size constraint: item must have footprint ≤ surface tile count
        if (itemTileCount !== undefined && itemTileCount > entry.ownerTileCount) continue;
        isOnSurface = true;
        if (entry.ownerZIndex > maxOwnerZIndex) maxOwnerZIndex = entry.ownerZIndex;
      }
    }
  }
  return { isOnSurface, maxOwnerZIndex };
}

/**
 * Pure helper – no React state deps.
 * Returns true ONLY if every footprint tile of an item is covered by a surface tile
 * in surfaceMap (same owner constraints as checkSurfaceOverlap).
 * Use this for the "fully inscribed" z-index boost: partial overlap does NOT qualify.
 *
 * @param footprintTiles     – admin-defined [dx, dy] offsets from centerTile, or null
 * @param centerTileX/Y      – item center tile (Math.round(depthComp.tileX/Y))
 * @param rawBaseTileX/Y     – raw bounding-box corner (used when footprintTiles is null)
 * @param widthTiles         – bounding-box width
 * @param heightTiles        – bounding-box height
 * @param surfaceMap         – surface tile map built from computeSurfaceTileMap
 * @param excludeFurnitureId – skip tiles owned by this id (prevents self-boost)
 * @param itemTileCount      – if set, skip surfaces where item is larger than surface
 */
export function isFullyOnSurface(
  footprintTiles: Array<Array<number>> | null | undefined,
  centerTileX: number,
  centerTileY: number,
  rawBaseTileX: number,
  rawBaseTileY: number,
  widthTiles: number,
  heightTiles: number,
  surfaceMap: SurfaceTileMap,
  excludeFurnitureId?: number,
  itemTileCount?: number,
  isMirrored = false,
): boolean {
  const tiles = footprintTiles && footprintTiles.length > 0
    ? placedFootprintTiles(footprintTiles, centerTileX, centerTileY, isMirrored)
    : placedRectTiles(rawBaseTileX, rawBaseTileY, widthTiles, heightTiles);
  if (tiles.length === 0) return false;
  // Each tile needs a valid (non-excluded, non-too-small) surface entry — with the
  // placement tolerance: a little of the edge may hang over (footprintTolerance).
  const offSurface = (tx: number, ty: number) => {
    const entry = surfaceMap.get(`${tx},${ty}`);
    if (!entry) return true;
    if (excludeFurnitureId !== undefined && entry.ownerFurnitureId === excludeFurnitureId) return true;
    return itemTileCount !== undefined && itemTileCount > entry.ownerTileCount;
  };
  return !exceedsTolerance(tiles, offSurface);
}

export type DepthComponents = {
  tileX: number;
  tileY: number;
  effectiveWidthTiles: number;
  effectiveHeightTiles: number;
  frontTileX: number;
  frontTileY: number;
  depthBase: number;
  depthFootprintOffset: number;
  depth: number;
};

export function getFurnitureDepthComponents(marker: SceneNode, widthTiles?: number | null, heightTiles?: number | null, isRug?: boolean): DepthComponents {
  const sprite = marker.children[0] as SceneNodeChild | undefined;
  const spriteWidth = sprite ? Math.abs(sprite.width) : 0;
  // Use sprite anchor (0.5, 1.0) as canonical footprint anchor for math.
  // This is marker.position in room-local coordinates.
  const x = marker.position.x;
  const y = marker.position.y;

  // Convert room-local isometric screen coordinates to logical tile coordinates.
  // Forward mapping is:
  // x = (tileX - tileY) * (TILE_WIDTH / 2)
  // y = (tileX + tileY) * (TILE_HEIGHT / 2)
  const halfTileWidth = TILE_WIDTH / 2;
  const halfTileHeight = TILE_HEIGHT / 2;
  const tileX = (x / halfTileWidth + y / halfTileHeight) / 2;
  const tileY = (y / halfTileHeight - x / halfTileWidth) / 2;

  // Use backend footprint when available (non-null). If NULL, estimate effective footprint
  // from sprite dimensions. Important: backend value 1 is valid (e.g., nightstand 1×1).
  const backendW = (widthTiles === null || widthTiles === undefined) ? null : Math.max(1, widthTiles);
  const backendH = (heightTiles === null || heightTiles === undefined) ? null : Math.max(1, heightTiles);

  let effectiveWidthTiles: number;
  let effectiveHeightTiles: number;

  if (isRug && sprite && backendW === null && backendH === null) {
    // Rug-specific: use isometric diamond geometry with both sprite dimensions
    const rugEst = estimateRugFootprintTiles(
      spriteWidth,
      Math.abs(sprite.height),
      sprite.__visualBaseOffsetX || 0,
    );
    effectiveWidthTiles = rugEst.width;
    effectiveHeightTiles = rugEst.height;
  } else if (backendW === null && backendH === null && sprite &&
             isWallMountedItem(readFurnitureData(marker, sprite)?.furniture_catalog)) {
    // Window/wall-mounted specific: width from widest opaque row, thickness always 1
    const maxOpaqueW = sprite.__maxOpaqueWidthPx || 0;
    const windowEst = estimateWindowFootprintTiles(maxOpaqueW);
    effectiveWidthTiles = windowEst.width;
    effectiveHeightTiles = windowEst.height;
  } else {
    // For regular furniture: use admin-defined footprint if available, else 1×1.
    // Heuristic sprite-based estimation was removed — admins should define footprints explicitly.
    effectiveWidthTiles = backendW !== null ? backendW : 1;
    effectiveHeightTiles = backendH !== null ? backendH : 1;
  }

  // Front tile (closest to camera) from effective footprint center.
  const frontTileX = tileX + (effectiveWidthTiles - 1) / 2;
  const frontTileY = tileY + (effectiveHeightTiles - 1) / 2;

  // Depth anchor shifted upward by footprint depth projection so large items don't over-cover.
  const depthBase = y;
  const depthFootprintOffset = ((effectiveHeightTiles - 1) * TILE_HEIGHT) / 2;
  const depth = depthBase - depthFootprintOffset + (frontTileX * 0.0001);
  return {
    tileX,
    tileY,
    effectiveWidthTiles,
    effectiveHeightTiles,
    frontTileX,
    frontTileY,
    depthBase,
    depthFootprintOffset,
    depth,
  };
}

export function getFurnitureDepth(marker: SceneNode, widthTiles?: number | null, heightTiles?: number | null, isRug?: boolean): number {
  const d = getFurnitureDepthComponents(marker, widthTiles, heightTiles, isRug);
  const sp = marker.children[0] as SceneNodeChild | undefined;
  const isMir = sp ? sp.scale.x < 0 : false;

  const fData = readFurnitureData(marker, sp);
  const fpTiles = fData?.furniture_catalog?.footprint_tiles;
  if (fpTiles && fpTiles.length > 0) {
    const centerTileX = Math.round(d.tileX);
    const centerTileY = Math.round(d.tileY);
    const halfTH = TILE_HEIGHT / 2;

    // Size-based epsilon tiebreaker: smaller items win ties over larger items.
    // 1/(tiles+1) gives bush(16)≈0.0000588 vs sofa(130)≈0.0000076 — enough to
    // break ties without disturbing the main depth order (one tile = 6.25 units).
    const sizeEpsilon = (1.0 / (fpTiles.length + 1)) * 0.001;

    // SMALL items (< 30 footprint tiles): use CENTER tile depth.
    //
    // Small decorative items (bush, vase, lamp…) often have back-heavy footprints
    // where every tile has dx+dy ≤ -1, so their front-most tile is always 1 step
    // behind their visual center. This causes the item to lose against an adjacent
    // large sofa arm even when their centers are at the same isometric depth.
    //
    // Using center depth is correct here: the item visually "stands at its center,"
    // and the 1-tile shift in the footprint is an artefact of how the catalog data
    // is defined, not a real depth difference. harbor-detection handles the case
    // where a small item is inside a concave footprint's gap.
    if (fpTiles.length < 30) {
      return (centerTileX + centerTileY) * halfTH + sizeEpsilon;
    }

    // LARGE items (≥ 30 footprint tiles): use FRONT-MOST footprint tile depth.
    //
    // For concave/L-shaped items like corner sofas, the front arm tile correctly
    // represents the closest-to-camera edge. Using center depth would be too high
    // (sofa center at sum=47 while front tile at sum=45) and would make items behind
    // the arm incorrectly render in front. mirrorFpOffset swaps dx↔dy which
    // preserves dx+dy, so the result is mirror-invariant.
    let maxDepth = -Infinity;
    for (const [dx, dy] of fpTiles) {
      const [tdx, tdy] = mirrorFpOffset(dx, dy, isMir);
      const depth = (centerTileX + tdx + centerTileY + tdy) * halfTH;
      if (depth > maxDepth) maxDepth = depth;
    }
    return maxDepth + sizeEpsilon;
  }

  // For regular items (no footprint_tiles): use the existing bounding-box formula.
  // Mirrored non-square items swap width↔height because a horizontal flip swaps
  // the iso-X and iso-Y axes.
  const effW = isMir ? d.effectiveHeightTiles : d.effectiveWidthTiles;
  const effH = isMir ? d.effectiveWidthTiles  : d.effectiveHeightTiles;
  const frontTileX = d.tileX + (effW - 1) / 2;
  const depthFpOffset = ((effH - 1) * TILE_HEIGHT) / 2;
  return d.depthBase - depthFpOffset + (frontTileX * 0.0001);
}

/**
 * Pure helper – checks whether an item's footprint (explicit footprint_tiles,
 * mirror-aware; or the rectangular occupied-base fallback) hits any key in
 * `occupiedMap`. Used during drag for floor/surface conflict tint and the
 * footprint overlay colour. Extracted 1:1 from four duplicated loops in
 * RoomCanvas.tsx onPointerMove.
 */
export function footprintHasConflict(
  footprintTiles: Array<Array<number>> | null | undefined,
  depth: DepthComponents,
  isMirrored: boolean,
  occupiedMap: { has(key: string): boolean },
): boolean {
  // With the placement tolerance: a little of the edge may overlap a neighbour.
  const occupied = (x: number, y: number) => occupiedMap.has(`${x},${y}`);
  if (footprintTiles && footprintTiles.length > 0) {
    const tiles = placedFootprintTiles(footprintTiles, Math.round(depth.tileX), Math.round(depth.tileY), isMirrored);
    return exceedsTolerance(tiles, occupied);
  }
  const base = getOccupiedBaseTile(depth, isMirrored);
  return exceedsTolerance(placedRectTiles(base.baseTileX, base.baseTileY, base.overlayWidthTiles, base.overlayHeightTiles), occupied);
}

export function getOccupiedBaseTile(depth: DepthComponents, isMirrored = false) {
  // depth.tileX/depth.tileY anchor is FRONT node of footprint (closest corner).
  // This matches legacy geometry that had better visual alignment in this room scene.
  //
  // KEY INSIGHT: In isometric projection, a horizontal screen-space flip (mirror)
  // swaps the iso-X and iso-Y axes.  Therefore the footprint's width_tiles and
  // height_tiles must be swapped for mirrored objects so that the occupied-tile
  // rectangle rotates correctly on the grid.
  const overlayWidthTiles  = isMirrored ? depth.effectiveHeightTiles : depth.effectiveWidthTiles;
  const overlayHeightTiles = isMirrored ? depth.effectiveWidthTiles  : depth.effectiveHeightTiles;

  const frontNodeTileX = Math.round(depth.tileX + 1e-6);
  const frontNodeTileY = Math.round(depth.tileY + 1e-6);
  const rawBaseTileX = frontNodeTileX - overlayWidthTiles;
  const rawBaseTileY = frontNodeTileY - overlayHeightTiles;
  const baseTileX = Math.max(0, Math.min(MAP_WIDTH_TILES - overlayWidthTiles, rawBaseTileX));
  const baseTileY = Math.max(0, Math.min(MAP_WIDTH_TILES - overlayHeightTiles, rawBaseTileY));
  const bottomCenterTileX = baseTileX + overlayWidthTiles / 2;
  const bottomCenterTileY = baseTileY + overlayHeightTiles;
  const bottomRightTileX = baseTileX + overlayWidthTiles;
  const bottomRightTileY = baseTileY + overlayHeightTiles;
  return {
    frontNodeTileX,
    frontNodeTileY,
    rawBaseTileX,
    rawBaseTileY,
    baseTileX,
    baseTileY,
    overlayWidthTiles,
    overlayHeightTiles,
    bottomCenterTileX,
    bottomCenterTileY,
    bottomRightTileX,
    bottomRightTileY,
  };
}

export type TileBounds = { minX: number; maxX: number; minY: number; maxY: number };

/**
 * Absolute tile-space bounding box of an item's footprint (mirror-aware).
 * Uses explicit footprint_tiles when present, else the rectangular occupied base.
 */
export function footprintTileBounds(
  depthComp: DepthComponents,
  footprintTiles: Array<Array<number>> | null | undefined,
  isMirrored: boolean,
): TileBounds {
  if (footprintTiles && footprintTiles.length > 0) {
    const cx = Math.round(depthComp.tileX);
    const cy = Math.round(depthComp.tileY);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [dx, dy] of footprintTiles) {
      const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
      const ax = cx + tdx;
      const ay = cy + tdy;
      if (ax < minX) minX = ax;
      if (ax > maxX) maxX = ax;
      if (ay < minY) minY = ay;
      if (ay > maxY) maxY = ay;
    }
    return { minX, maxX, minY, maxY };
  }
  const base = getOccupiedBaseTile(depthComp, isMirrored);
  return {
    minX: base.baseTileX,
    maxX: base.baseTileX + base.overlayWidthTiles - 1,
    minY: base.baseTileY,
    maxY: base.baseTileY + base.overlayHeightTiles - 1,
  };
}

/**
 * Painter's separating-axis rule for tile-based isometric scenes: `a` is
 * unambiguously in front of `b` when a's footprint lies wholly beyond b's along
 * one iso axis while the two still overlap on the other. Greater tileX (or tileY)
 * is closer to the camera, so no depth heuristic may contradict this.
 *
 * Returns false for diagonal or interleaved footprints — those genuinely have no
 * separating axis and stay with the proximity heuristic.
 */
export function isUnambiguouslyInFront(a: TileBounds, b: TileBounds): boolean {
  const overlapY = a.minY <= b.maxY && b.minY <= a.maxY;
  const overlapX = a.minX <= b.maxX && b.minX <= a.maxX;
  if (overlapY && a.minX > b.maxX) return true;
  if (overlapX && a.minY > b.maxY) return true;
  return false;
}

/**
 * Build a SurfaceTileMap from all currently placed furniture that have surface_tiles defined.
 * The map key is "tileX,tileY" in isometric tile coords; value is the owner marker + its
 * current zIndex (so callers can apply SURFACE_ZINDEX_BOOST relative to it).
 * @param excludeFurnitureId - optional furniture id to skip (e.g. the item being dragged)
 */
export function computeSurfaceTileMap(markers: Map<number, SceneNode>, excludeFurnitureId?: number): SurfaceTileMap {
  const map: SurfaceTileMap = new Map();
  for (const [fId, marker] of markers.entries()) {
    if (fId === excludeFurnitureId) continue;
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const fData = readFurnitureData(marker, sp);
    const surfaceTilesArr = fData?.furniture_catalog?.surface_tiles;
    if (!surfaceTilesArr || surfaceTilesArr.length === 0) continue;
    const { w, h, isRug } = getTileDims(fData.furniture_catalog);
    const depth = getFurnitureDepthComponents(marker, w, h, isRug);
    const centerTileX = Math.round(depth.tileX);
    const centerTileY = Math.round(depth.tileY);
    const isMir = sp ? sp.scale.x < 0 : false;
    const ownerTileCount = surfaceTilesArr.length;
    const ownerPlacementMode: PlacementMode = toPlacementMode(fData.placement_mode);
    for (const [dx, dy] of surfaceTilesArr) {
      // Mirror swaps iso-X and iso-Y axes → swap dx/dy when mirrored
      const [tdx, tdy] = isMir ? [dy, dx] : [dx, dy];
      const absTileX = centerTileX + tdx;
      const absTileY = centerTileY + tdy;
      const key = `${absTileX},${absTileY}`;
      // TOP-MOST SURFACE WINS. Two items' surface areas can overlap in tile space
      // when they are placed FLUSH against each other (e.g. a nightstand pushed
      // against a bed — the bed's 84-tile surface reaches over the nightstand's).
      // Plain last-writer-wins would hand the contested tile to whichever marker
      // Map iteration reached last (insertion order = furniture id), so an item
      // resting on the nightstand could resolve its owner to the BED and be lifted
      // only above the bed — i.e. BELOW its real owner, rendering behind it.
      // Keeping the higher-zIndex owner is both deterministic (id-order independent)
      // and physically right: an item rests on whichever surface renders on top.
      const prev = map.get(key);
      if (prev && prev.ownerZIndex >= marker.zIndex) continue;
      map.set(key, {
        ownerMarker: marker,
        ownerZIndex: marker.zIndex,
        ownerFurnitureId: fId,
        ownerTileCount,
        ownerPlacementMode,
      });
    }
  }
  return map;
}

/**
 * Build a footprint tile map from all currently placed non-window, non-rug furniture.
 * The map key is "tileX,tileY" in isometric tile coords; value is the owner furniture id.
 * Used to detect footprint overlap conflicts during furniture drag.
 * @param excludeFurnitureId - optional furniture id to skip (e.g. the item being dragged)
 */
export function computeFootprintTileMap(markers: Map<number, SceneNode>, excludeFurnitureId?: number, surfaceItemsOnly = false): Map<string, number> {
  const map = new Map<string, number>();
  for (const [fId, marker] of markers.entries()) {
    if (fId === excludeFurnitureId) continue;
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const fData = readFurnitureData(marker, sp);
    if (!fData) continue;
    const { w, h, isRug, isWindow: fIsWindow } = getTileDims(fData.furniture_catalog);
    if (isRug || fIsWindow) continue;
    // Filter by level using placement_mode (authoritative, z-index fluctuates during drag).
    const itemPlacementMode = toPlacementMode(fData.placement_mode);
    if (surfaceItemsOnly) {
      if (!isSurface(itemPlacementMode)) continue; // include only surface items
    } else {
      if (!participatesInFootprint(itemPlacementMode)) continue; // skip items that don't occupy floor tiles
    }
    const depth = getFurnitureDepthComponents(marker, w, h, isRug);
    const isMir = sp ? sp.scale.x < 0 : false;
    const fpTiles = fData.furniture_catalog?.footprint_tiles;
    if (fpTiles && fpTiles.length > 0) {
      const centerTileX = Math.round(depth.tileX);
      const centerTileY = Math.round(depth.tileY);
      for (const [dx, dy] of fpTiles) {
        const [tdx, tdy] = isMir ? [dy, dx] : [dx, dy];
        map.set(`${centerTileX + tdx},${centerTileY + tdy}`, fId);
      }
    } else {
      const base = getOccupiedBaseTile(depth, isMir);
      for (let tx = 0; tx < base.overlayWidthTiles; tx++) {
        for (let ty = 0; ty < base.overlayHeightTiles; ty++) {
          map.set(`${base.baseTileX + tx},${base.baseTileY + ty}`, fId);
        }
      }
    }
  }
  return map;
}

export function recomputeAllZIndexes(
  markers: Map<number, SceneNode>,
  dragState: {
    isDragging: boolean;
    interiorId: number;
    marker: SceneNode;
    startX: number;
    startY: number;
    startWorldX: number;
    startWorldY: number;
    offsetX: number;
    offsetY: number;
    origX: number;
    origY: number;
    previousWindowSnap?: import('./windowSnap').WindowSnapResult;
  } | null,
  draggingCarriedIds: Set<number>,
  logFurnitureDiagnostics: () => void,
): void {
  // The currently dragged item's marker is at a temporary mid-drag position.
  // Its surface tiles and z-index are managed by the drag code — exclude it
  // from both z-index recomputation and surface map building so that other
  // items (e.g. a vase on a table) don't get falsely boosted relative to
  // the dragged item's transient position.
  const draggedFurnitureId = dragState?.interiorId;
  // Pass 1: base depths (position-only, no surface boost)
  for (const marker of markers.values()) {
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const fData = readFurnitureData(marker, sp);
    if (!fData) continue;
    // Skip items being dragged or carried — their z-index is managed by the drag code
    if (draggedFurnitureId !== undefined && fData.id === draggedFurnitureId) continue;
    if (draggingCarriedIds.size > 0 && draggingCarriedIds.has(fData.id)) continue;
    const { w, h, isRug } = getTileDims(fData.furniture_catalog);
    const baseDepth = getFurnitureDepth(marker, w, h, isRug);
    if (isRug) {
      // Rugs use rug_z_order as a tie-breaker: the most recently moved rug appears on top
      // of other rugs it overlaps. The 0.001 coefficient is intentionally small — it acts as
      // a tie-breaker for overlapping rugs (same/nearby depth) without overriding the
      // position-based depth ordering of non-overlapping rugs farther apart.
      marker.zIndex = baseDepth + (fData.rug_z_order ?? 0) * 0.001;
    } else {
      marker.zIndex = baseDepth;
    }
    // Preserve elevated z-index for conflict-red items so they're always visible
    if (sp && sceneTintNumber(sp.tint) === 0xFF4444) {
      marker.zIndex = Math.max(marker.zIndex, CONFLICT_FLOOR_ZINDEX);
    }
  }
  // Passes 2+: iteratively boost items on surfaces until stable.
  // Multiple iterations handle nested surfaces: item A on surface of item B which is
  // itself on surface of item C. Each iteration propagates one more level of boost so
  // that: C.zIndex = base, B.zIndex = base+BOOST, A.zIndex = base+2×BOOST.
  // Encapsulated so it can run BEFORE and AGAIN AFTER the concavity pass (see below).
  const propagateSurfaceBoost = () => {
  for (let level = 0; level < MAX_SURFACE_LEVELS; level++) {
    // Rebuild surface map every iteration so it captures updated (boosted) zIndexes
    // from the previous iteration — this is what makes nesting work correctly.
    // Exclude the dragged item so its mid-drag surface tiles don't affect other items.
    const surfaceMap = computeSurfaceTileMap(markers, draggedFurnitureId);
    if (surfaceMap.size === 0) break;
    let anyChange = false;
    for (const [fId, marker] of markers.entries()) {
      const sp = marker.children[0] as SceneNodeChild | undefined;
      const fData = readFurnitureData(marker, sp);
      if (!fData) continue;
      // Skip dragged/carried items — their z-index is managed by the drag code
      if (draggedFurnitureId !== undefined && fData.id === draggedFurnitureId) continue;
      if (draggingCarriedIds.size > 0 && draggingCarriedIds.has(fData.id)) continue;
      // Windows/wall-mounted are wall-mounted and use WINDOW_VERTICAL_OFFSET in their marker.y.
      // getOccupiedBaseTile doesn't account for this offset, so tile coords are wrong
      // for windows/wall-mounted and they may falsely match surface tiles → skip surface boost for windows/wall-mounted.
      if (isWallMountedItem(fData.furniture_catalog)) continue;
      const { w, h, isRug } = getTileDims(fData.furniture_catalog);
      const depthComp = getFurnitureDepthComponents(marker, w, h, isRug);
      const isMirrored = (sp?.scale?.x ?? 1) < 0;
      const base = getOccupiedBaseTile(depthComp, isMirrored);
      const fpTiles = fData.furniture_catalog?.footprint_tiles;
      const itemTileCount = fpTiles?.length ?? (depthComp.effectiveWidthTiles * depthComp.effectiveHeightTiles);
      const { isOnSurface, maxOwnerZIndex } = checkSurfaceOverlap(
        base.rawBaseTileX, base.rawBaseTileY,
        base.overlayWidthTiles, base.overlayHeightTiles,
        surfaceMap,
        fId,           // exclude own surface tiles to prevent self-boost
        itemTileCount, // size constraint: item must be ≤ surface tile count
      );
      const itemPlacementMode = toPlacementMode(fData.placement_mode);
      if (isOnSurface) {
        // Always require full inscription for z-index boost, regardless of placement_mode.
        // For 'surface'-mode items: isFullyOnSurface guards against stale placement_mode
        // (e.g. an item was saved as 'surface' but the surface item later moved away —
        // without this check, a partial overlap would incorrectly boost the item again
        // whenever any surface comes near).
        // For 'floor'-mode items: never boost.
        let shouldBoost: boolean;
        if (!isSurface(itemPlacementMode)) {
          // Explicitly on floor — never boost, even if physically on a surface.
          shouldBoost = false;
        } else {
          // 'surface' or legacy null: require full inscription.
          const centerTileX = Math.round(depthComp.tileX);
          const centerTileY = Math.round(depthComp.tileY);
          shouldBoost = isFullyOnSurface(
            fData.furniture_catalog?.footprint_tiles,
            centerTileX, centerTileY,
            base.rawBaseTileX, base.rawBaseTileY,
            base.overlayWidthTiles, base.overlayHeightTiles,
            surfaceMap, fId, itemTileCount, isMirrored,
          );
        }
        if (shouldBoost) {
          // Include a tiny fraction of the item's own depth so siblings on the same
          // surface are sorted correctly relative to each other (the 0.0001 scale keeps
          // the total delta well below the ~12.5px inter-tile depth step, preserving
          // the "inherit owner's depth band" property).
          const newZ = maxOwnerZIndex + SURFACE_PLACEMENT_DELTA + depthComp.depth * 0.0001;
          if (marker.zIndex !== newZ) {
            marker.zIndex = newZ;
            anyChange = true;
          }
        }
      }
    }
    if (!anyChange) break; // stable — no more levels to propagate
  }
  };
  // First propagation: establish surface ordering before concavity runs.
  propagateSurfaceBoost();
  // Pairwise concavity correction pass.
  //
  // Isometric depth sorting assigns ONE zIndex per sprite. For convex items this is
  // sufficient, but items with concave footprints (e.g. L-shaped sofas) have a front
  // arm (close to camera) and a back section (far from camera). A single zIndex
  // based on the front arm makes the whole sprite render on top — including
  // back-section pixels that should be behind items standing near the back.
  //
  // Two sub-passes run here for every item with footprint_tiles (≥ 20 tiles):
  //
  // 1. HARBOR: item whose center tile is inside the concave bbox but NOT on any
  //    footprint tile (sitting in the empty gap) → boost above the concave item.
  //    Only runs for concave shapes (missingRatio ≥ 25%, e.g. L-shaped sofa).
  //
  // 2. FRONT-PROXIMITY: item outside the bbox → compute frontWorldSum (world depth
  //    sum of the large item's front-most footprint tile). Boost the small item if
  //    frontGap = frontWorldSum − smallCenterSum ≤ ARM_PROXIMITY (=GRID_SUBDIVISION=4).
  //    This handles: items genuinely in front (frontGap ≤ 0), items just beside the
  //    front edge (0 < frontGap ≤ 4, e.g. nightstand beside bed headboard), and
  //    correctly rejects items far behind (frontGap > 4, e.g. bamboo bush behind
  //    sofa). Using frontWorldSum instead of nearestWorldSum avoids the "back-corner
  //    false positive" where a back tile of the large item has lower depth sum than
  //    a distant item, incorrectly triggering a boost.
  for (const [, largeMarker] of markers.entries()) {
    const largeSp = largeMarker.children[0] as SceneNodeChild | undefined;
    const largeData = readFurnitureData(largeMarker, largeSp);
    if (!largeData) continue;
    if (draggedFurnitureId !== undefined && largeData.id === draggedFurnitureId) continue;
    if (draggingCarriedIds.size > 0 && draggingCarriedIds.has(largeData.id)) continue;
    const largeFpTiles = largeData.furniture_catalog?.footprint_tiles;
    // Only large items (≥ 20 footprint tiles) run as the outer item.
    // Small items like nightstands (≈ 9 tiles) can have high missingRatio due to
    // irregular but small footprints — letting them be outer incorrectly boosts
    // large items (beds, sofas) above themselves.
    if (!largeFpTiles || largeFpTiles.length < 20) continue;
    const largeMirrored = (largeSp?.scale?.x ?? 1) < 0;
    const { w: lw, h: lh, isRug: lIsRug } = getTileDims(largeData.furniture_catalog);
    const largeDepthComp = getFurnitureDepthComponents(largeMarker, lw, lh, lIsRug);
    const largeCTX = Math.round(largeDepthComp.tileX);
    const largeCTY = Math.round(largeDepthComp.tileY);
    // Build mirrored footprint tile set and bounding box.
    const largeFpSet = new Set<string>();
    let minTdx = Infinity, maxTdx = -Infinity, minTdy = Infinity, maxTdy = -Infinity;
    let maxRelTileSum = -Infinity; // max(tdx+tdy) = front-most footprint tile offset from anchor
    for (const [dx, dy] of largeFpTiles) {
      const [tdx, tdy] = mirrorFpOffset(dx, dy, largeMirrored);
      largeFpSet.add(`${tdx},${tdy}`);
      if (tdx < minTdx) minTdx = tdx;
      if (tdx > maxTdx) maxTdx = tdx;
      if (tdy < minTdy) minTdy = tdy;
      if (tdy > maxTdy) maxTdy = tdy;
      const s = tdx + tdy; if (s > maxRelTileSum) maxRelTileSum = s;
    }
    // Concavity check: harbor detection only applies to meaningfully concave shapes.
    // A slightly irregular 4×4 bush has ≈ 6–16% missing → not concave.
    // An L-shaped sofa has ≈ 44% missing → concave.
    // Nearest-tile comparison runs for ALL items with footprint_tiles (including
    // rectangular beds) because even convex large items can have their back-section
    // pixels wrongly cover a small item placed beside them.
    const bbArea = (maxTdx - minTdx + 1) * (maxTdy - minTdy + 1);
    const isConcave = (bbArea - largeFpTiles.length) / bbArea >= 0.25;
    // Maximum useful search radius: bbox diagonal + 4 tile buffer.
    const maxSpread = (maxTdx - minTdx) + (maxTdy - minTdy) + 4;
    // Absolute tile bbox of the large item, for the separating-axis veto below.
    const largeBounds: TileBounds = {
      minX: largeCTX + minTdx, maxX: largeCTX + maxTdx,
      minY: largeCTY + minTdy, maxY: largeCTY + maxTdy,
    };
    for (const [, smallMarker] of markers.entries()) {
      const smallSp = smallMarker.children[0] as SceneNodeChild | undefined;
      const smallData = readFurnitureData(smallMarker, smallSp);
      if (!smallData || smallData.id === largeData.id) continue;
      if (draggedFurnitureId !== undefined && smallData.id === draggedFurnitureId) continue;
      if (draggingCarriedIds.size > 0 && draggingCarriedIds.has(smallData.id)) continue;
      // NEVER let concavity touch a surface item. Both boosts below assign an ABSOLUTE
      // zIndex (largeMarker.zIndex + own depth * 0.00001), which destroys the
      // "sits on its owner" relationship: a vase and the nightstand it stands on would
      // both be clobbered to the bed's band and then ordered by their OWN depth. A
      // surface item is drawn HIGHER on screen than its owner, so it has the SMALLER
      // depth — it would land BELOW the very nightstand it rests on.
      // A surface item's z belongs to propagateSurfaceBoost (owner + delta), full stop.
      // Previously this was only healed by the post-concavity propagateSurfaceBoost
      // re-run, which is skipped for dragged/carried items — leaving the item stranded.
      if (isSurface(toPlacementMode(smallData.placement_mode))) continue;
      const { w: sw, h: sh, isRug: sIsRug } = getTileDims(smallData.furniture_catalog);
      const smallDepthComp = getFurnitureDepthComponents(smallMarker, sw, sh, sIsRug);
      const relDx = Math.round(smallDepthComp.tileX) - largeCTX;
      const relDy = Math.round(smallDepthComp.tileY) - largeCTY;
      // Spatial pre-filter: skip items clearly outside any meaningful range.
      if (Math.abs(relDx) + Math.abs(relDy) > maxSpread) continue;
      const isInsideBbox = relDx >= minTdx && relDx <= maxTdx && relDy >= minTdy && relDy <= maxTdy;
      if (isInsideBbox) {
        // Harbor: inside bbox but NOT on a footprint tile → boost above concave item.
        // Only meaningful for concave shapes (e.g. L-shaped sofa gap).
        // Rectangular items (beds, wardrobes) are skipped here.
        if (!isConcave) continue;
        if (largeFpSet.has(`${relDx},${relDy}`)) continue;
        if (smallMarker.zIndex <= largeMarker.zIndex) {
          smallMarker.zIndex = largeMarker.zIndex + smallDepthComp.depth * 0.00001 + 0.001;
        }
      } else {
        // OUTSIDE-BBOX BOOST: compare the small item to the large item's FRONT tile.
        //
        // Key insight: the anchor (tileX/tileY) is the FRONT corner of the sprite in
        // isometric space. All footprint tiles go backward from it, so every tile has
        // tdx+tdy ≤ maxRelTileSum (typically -2 for 12×13 items). frontWorldSum is the
        // world-depth sum of the large item's closest-to-camera footprint tile.
        //
        // Condition: boost if the small item is within ARM_PROXIMITY tiles of the
        // large item's front (frontGap ≤ ARM_PROXIMITY). This covers:
        //   frontGap ≤ 0 → small item is ahead of (or equal to) the large item's front
        //   0 < frontGap ≤ ARM_PROXIMITY → small item is a few tiles behind the front
        //     edge (e.g. nightstand beside bed headboard needs this tolerance)
        //
        // ARM_PROXIMITY: how many tile-coordinate units behind the large item's
        // front a small item can be and still get boosted above it.
        //
        // Set to GRID_SUBDIVISION (=4 at GS=4). This is tight enough to reject
        // items genuinely behind the large item (e.g. bamboo bush behind sofa,
        // frontGap=5–6) while still covering the nightstand-beside-bed-headboard
        // case (frontGap=4). The value scales naturally with GRID_SUBDIVISION
        // because tile coordinates scale with it but footprint offsets do not.
        //
        // Mirror-invariant: mirrorFpOffset swaps dx↔dy, preserving dx+dy sums.
        //
        // effectiveProximity: large items (≥ 20 fp tiles) acting as "small" only
        // get boosted if genuinely in front (frontGap ≤ 0). ARM_PROXIMITY
        // tolerance is reserved for truly small items (nightstands, vases, bushes).
        // SEPARATING-AXIS VETO (must run before the proximity heuristic).
        //
        // frontGap is evaluated independently per ordered pair, and BOTH directions
        // of a pair can satisfy it — each item then claims to be in front of the
        // other and the winner is decided by Map insertion order, not geometry.
        // Real case (map_object 204): chair at tile (18,5) beside bed at (12,13).
        //   large=bed  → frontGap 23−23 =  0 ≤ 0 → chair boosted above bed
        //   large=chair→ frontGap 22−25 = −3 ≤ 0 → bed boosted above chair (wins)
        // The chair rendered behind the bed purely because the bed is written last.
        //
        // Footprint separation settles it: the chair occupies x∈[12,17] and the bed
        // x∈[0,11] with overlapping y, so the chair is strictly closer to the camera.
        // When the large item is the one unambiguously in front, no proximity boost
        // may lift the small item above it. This only ever REMOVES boosts, so it
        // cannot introduce new false positives.
        const smallMirrored = (smallSp?.scale?.x ?? 1) < 0;
        const smallBounds = footprintTileBounds(
          smallDepthComp,
          smallData.furniture_catalog?.footprint_tiles,
          smallMirrored,
        );
        if (isUnambiguouslyInFront(largeBounds, smallBounds)) continue;
        const ARM_PROXIMITY = GRID_SUBDIVISION;
        const smallFpCount = smallData.furniture_catalog?.footprint_tiles?.length ?? 0;
        const effectiveProximity = smallFpCount >= 20 ? 0 : ARM_PROXIMITY;
        const smallCenterSum = Math.round(smallDepthComp.tileX) + Math.round(smallDepthComp.tileY);
        const frontWorldSum = largeCTX + largeCTY + maxRelTileSum;
        const frontGap = frontWorldSum - smallCenterSum;
        if (frontGap <= effectiveProximity && smallMarker.zIndex <= largeMarker.zIndex) {
          smallMarker.zIndex = largeMarker.zIndex + smallDepthComp.depth * 0.00001 + 0.001;
        }
      }
    }
  }
  // Re-run the surface propagation AFTER concavity. The concavity pass can raise a
  // surface OWNER's zIndex (e.g. a nightstand boosted above a nearby bed's front tile
  // when it's pushed flush into a window corner). That would strand the items sitting
  // ON that owner — candles on the nightstand — BELOW it, so they render hidden behind
  // their own owner ("surface items rendered off the nightstand near a window" bug).
  // This second pass re-lifts every fully-inscribed surface item above its (possibly
  // raised) owner. It only ever moves surface items, so the concavity ordering of floor
  // items is preserved, and it's a no-op when no owner was raised.
  propagateSurfaceBoost();
  // Auto-log all interior object states after every z-index recomputation (dev only)
  if (import.meta.env.DEV) logFurnitureDiagnostics();
}

/**
 * zIndex for the walking character, sorted by the SAME rule as the furniture.
 *
 * The obvious formula — `(round(tileX) + round(tileY)) * halfTH` — is only the
 * *proximity half* of how this scene orders things, and using it alone is what
 * made the character walk through sofas. Furniture z is not a pure function of
 * position: `recomputeAllZIndexes` gives a large item ONE zIndex taken from its
 * front-most footprint tile, then pass 2 lifts neighbours over it, gated by the
 * separating-axis veto. A 156-tile sofa therefore sits at the depth of its
 * nearest corner and legitimately covers everything with a smaller depth sum —
 * including a character standing in front of the sofa's far end. Any second,
 * simpler rule for actors can only disagree with the first one somewhere.
 *
 * So the actor is sorted as an ordinary item: start from the proximity depth,
 * then run the pass-2 comparisons against every furniture marker. Only the
 * actor's own z is produced — furniture markers are never touched, so this is
 * safe to call every frame while `recomputeAllZIndexes` stays scene-change-only.
 *
 * Footprint: the anchor sub-tile, 1x1. That is not an approximation of the
 * sprite (which is ~13 sub-tiles wide) but a deliberate match with the collision
 * model — `buildWalkGrid` tests walkability on the anchor tile alone, and sprite
 * overhang is handled separately by EDGE_MARGIN_TILES. Depth sorting in an
 * isometric scene is about the area on the FLOOR, exactly as a tall wardrobe is
 * sorted by its base and not by its height; giving the actor a depth footprint
 * bigger than his collision footprint would recreate the very disagreement
 * between "where he stands" and "where he sorts" that this function removes.
 *
 * Two deliberate differences from the furniture caller:
 *   - proximity tolerance is 0, not ARM_PROXIMITY. That tolerance exists so a
 *     nightstand pushed against a bed's headboard still draws in front of it;
 *     applied to an actor it would draw him over a sofa he is standing *behind*
 *     by up to 4 sub-tiles — legs in front of the backrest, the artefact the
 *     animation critics reject on sight.
 *   - rugs and wall-mounted items never occlude the actor: he walks on top of
 *     the former and in front of the latter, and neither owns floor volume.
 */
export function computeActorZIndex(
  markers: Map<number, SceneNode>,
  tileX: number,
  tileY: number,
): number {
  const halfTH = TILE_HEIGHT / 2;
  const actorTileX = Math.round(tileX);
  const actorTileY = Math.round(tileY);
  const actorSum = actorTileX + actorTileY;
  // Baseline: the proximity heuristic, identical to a small item's depth.
  let z = actorSum * halfTH;
  const actorBounds: TileBounds = {
    minX: actorTileX, maxX: actorTileX,
    minY: actorTileY, maxY: actorTileY,
  };

  for (const marker of markers.values()) {
    const sp = marker.children[0] as SceneNodeChild | undefined;
    const data = readFurnitureData(marker, sp);
    if (!data) continue;
    // Surface items ride their owner's z; an actor can never be "between" a vase
    // and the table it stands on, so comparing against them adds nothing.
    if (!participatesInFootprint(toPlacementMode(data.placement_mode))) continue;
    const catalog = data.furniture_catalog;
    const { w, h, isRug } = getTileDims(catalog);
    if (isRug || isWallMountedItem(catalog)) continue;

    const depthComp = getFurnitureDepthComponents(marker, w, h, isRug);
    const isMirrored = sp ? sp.scale.x < 0 : false;
    const fpTiles = catalog?.footprint_tiles;
    const bounds = footprintTileBounds(depthComp, fpTiles, isMirrored);

    // Already clear of this item AND of its surface stack? Nothing to decide.
    if (z > marker.zIndex + SURFACE_STACK_CLEARANCE) continue;

    // Separating-axis veto: when the furniture's footprint is wholly closer to
    // the camera, no heuristic may lift the actor over it. Runs first because it
    // only ever removes boosts and so cannot invent new overlaps.
    if (isUnambiguouslyInFront(bounds, actorBounds)) continue;

    // Same veto, but tile-accurate. A concave item (L-sofa, corner desk) has a
    // bbox that swallows its own notch, so the bbox test above stays silent even
    // when *every* occupied tile is in front of the actor standing in that notch.
    // Compare the real tiles: if none of them is behind the actor on either axis,
    // the whole item is in front and the harbor rule below must not fire.
    if (fpTiles && fpTiles.length > 0) {
      const cx = Math.round(depthComp.tileX);
      const cy = Math.round(depthComp.tileY);
      const whollyInFront = fpTiles.every(([dx, dy]) => {
        const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
        return cx + tdx >= actorTileX && cy + tdy >= actorTileY;
      });
      if (whollyInFront) continue;
    }

    let boost = isUnambiguouslyInFront(actorBounds, bounds);
    if (!boost && fpTiles && fpTiles.length > 0) {
      const cx = Math.round(depthComp.tileX);
      const cy = Math.round(depthComp.tileY);
      const relDx = actorTileX - cx;
      const relDy = actorTileY - cy;
      const insideBbox =
        relDx >= bounds.minX - cx && relDx <= bounds.maxX - cx &&
        relDy >= bounds.minY - cy && relDy <= bounds.maxY - cy;
      if (insideBbox) {
        // Harbor: standing in the gap of a concave shape (the well of an L-sofa)
        // is standing in FRONT of the arm that wraps around it. Walkability let
        // him in here precisely because the tile is not a footprint tile.
        const onFootprint = fpTiles.some(([dx, dy]) => {
          const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
          return tdx === relDx && tdy === relDy;
        });
        boost = !onFootprint;
      } else {
        // Outside the bbox: compare against the item's front-most footprint tile,
        // the same quantity its own depth is built from. Tolerance 0 — see above.
        let maxRelTileSum = -Infinity;
        for (const [dx, dy] of fpTiles) {
          const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
          const s = tdx + tdy;
          if (s > maxRelTileSum) maxRelTileSum = s;
        }
        boost = cx + cy + maxRelTileSum - actorSum <= 0;
      }
    }
    // Stepping over the item means stepping over everything that rides it.
    if (boost) z = Math.max(z, marker.zIndex + SURFACE_STACK_CLEARANCE);
  }
  return z;
}

/**
 * Clear red conflict tint from items that no longer have a footprint overlap.
 * Call after any furniture removal so items that were red only because of the
 * now-removed item return to their normal appearance.
 */
export function clearResolvedConflictTints(markers: Map<number, SceneNode>): void {
  for (const [fId, marker] of markers.entries()) {
    const sp = marker.children[0] as SceneNodeChild | undefined;
    if (!sp) continue;
    if (sceneTintNumber(sp.tint) !== 0xFF4444) continue; // already clear
    const fData = readFurnitureData(marker, sp);
    if (!fData) continue;
    const { w, h, isRug, isWindow: fIsWindow } = getTileDims(fData.furniture_catalog);
    if (isRug || fIsWindow) continue;

    const pm = toPlacementMode(fData.placement_mode);
    // Build the map EXCLUDING this item. A shared map has an overwrite problem:
    // if this item's tiles were written last in the loop they overwrite other items'
    // tiles, so the `otherId !== fId` check never fires → conflict missed.
    // Excluding self guarantees every hit in the map belongs to another item.
    const checkMap = isSurface(pm)
      ? computeFootprintTileMap(markers, fId, true)
      : computeFootprintTileMap(markers, fId, false);

    const depthComp = getFurnitureDepthComponents(marker, w, h, false);
    const fpTiles = fData.furniture_catalog?.footprint_tiles;
    const hasConflict = footprintHasConflict(fpTiles, depthComp, sp.scale.x < 0, checkMap)
      || (!isSurface(pm) && footprintOutOfRoom(fpTiles, depthComp, sp.scale.x < 0));

    if (!hasConflict) {
      sp.tint = 0xFFFFFF;
      sp.alpha = 1;
      marker.zIndex = getFurnitureDepth(marker, w, h, isRug);
    }
  }
}
